import React, { useRef, useCallback, useEffect, useMemo, useState } from 'react';
import { ReportViewModel, ReportColumn } from 'types/report';
import './NTLayout.scss';
import { prefixClass } from 'lib/utils';

import { useNTContext } from '../NTContext';
import { useNTUtilsContext } from '../NTUtilsContext';
import useColumnRenderer from '../hooks/useColumnRenderer';
import { useQueryState } from 'hooks/useQueryState';

const TABLE_TYPE_KS = 'KS' as const;
const TABLE_TYPE_CSV = 'CSV' as const;
const TABLE_TYPE_JSON = 'JSON' as const;

type TableType = typeof TABLE_TYPE_KS | typeof TABLE_TYPE_CSV | typeof TABLE_TYPE_JSON;

type NTLayoutProps = {
  data?: ReportViewModel | any | null;
  type?: TableType;
  fixFC?: boolean;
  fixLC?: boolean;
  searchTerm?: string;
  firstColumnCellisHeader?: boolean;
  parentRef?: React.RefObject<HTMLElement>;
};

const NTLayout = ({
  data,
  type = TABLE_TYPE_KS,
  fixFC = false,
  fixLC = false,
  firstColumnCellisHeader = false,
  parentRef,
  searchTerm,
}: NTLayoutProps) => {
  const { columns, setColumns, filters, budgetAvailable } = useNTContext();
  const { actions, capabilities } = useNTUtilsContext();
  const [reverse] = useQueryState<string>('reverse');

  // Ref for measuring container height for virtualization
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerHeight, setContainerHeight] = React.useState(600); // Default height

  // Measure container height for virtualization
  useEffect(() => {
    const updateHeight = () => {
      if (containerRef.current) {
        const height = containerRef.current.clientHeight;
        if (height > 0) {
          setContainerHeight(height);
        }
      }
    };

    updateHeight();
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, []);

  if (!data) {
    return <div>No data Provided</div>;
  }

  const columnsData: ReportColumn[] | any[] | null = useMemo(() => {
    switch (type) {
      case TABLE_TYPE_KS:
        return data.columns ? data.columns : [];
      case TABLE_TYPE_CSV:
      case TABLE_TYPE_JSON:
        return null;
      default:
        return null;
    }
  }, [data.columns, type]);

  if (!columnsData) {
    return <div>Not Supported format</div>;
  }

  /**
   * Creates a style element with CSS rules for table rows based on the provided column data.
   * We don't have a physical row. Instead we have series of column cells that conform a Virtual Row
   *
   * @param columnsData - An array of column data objects.
   * @returns A style element with the generated CSS rules.
   */

  const createRowStyles = (columnsData: any[]) => {
    const style = document.createElement('style');

    // OPTIMIZATION: Use generic selectors instead of individual row rules
    // This significantly reduces CSS rule count and parsing time
    // Instead of N rules for N rows, we use ~5 generic rules
    const rowRules = `
      /* Base row styles - applies to all rows */
      [data-row-id] { /* base styles */ }

      /* Hover styles for non-selected rows */
      [data-row-id]:not(.selected).hovered {
        background-color: var(--ks-table-highlight);
      }

      /* Hover styles for total rows */
      [data-row-id].total.hovered {
        background-color: var(--ks-table-highlight);
      }

      /* Hidden row styles */
      [data-row-id].hidden {
        display: none;
      }
    `;

    // Column styles - generic rules for all columns
    const columnRules = `
      /* Base column styles - applies to all columns */
      [data-column-id] { /* base column styles */ }

      /* Highlight the intersection cell with a special style */
      .nt-cell.selected-cell {
        background-color: hsl(133, 41.20%, 80.00%) !important;
      }
    `;

    style.textContent = rowRules + columnRules;
    return style;
  };

  const applySearch = useCallback(
    (searchTerm: string | null | undefined) => {
      const rows = document.querySelectorAll('[data-row-id]');
      rows.forEach(row => {
        const rowId = row.getAttribute('data-row-id');
        if (rowId) {
          row.classList.remove('hidden');
        }
      });
      if (!searchTerm) {
        return;
      }
      rows.forEach(row => {
        const rowId = row.getAttribute('data-row-id');
        const rowData = columnsData && columnsData[0] && columnsData[0].data.find((item: any) => item.id === rowId);

        if (!rowData) {
          row.classList.add('hidden');
          return;
        }

        // Check all searchable fields
        const searchTermLower = searchTerm.toLowerCase();
        const amountTitleMatch = rowData.amountTitle?.toLowerCase().includes(searchTermLower);
        const percentTitleMatch = rowData.percentTitle?.toLowerCase().includes(searchTermLower);
        const glCodeMatch = rowData.glCode && String(rowData.glCode).toLowerCase().includes(searchTermLower);
        const refTitleMatch = rowData.refTitle?.toLowerCase().includes(searchTermLower);

        if (amountTitleMatch || percentTitleMatch || glCodeMatch || refTitleMatch) {
          row.classList.remove('hidden');
        } else {
          row.classList.add('hidden');
        }
      });
    },
    [columnsData]
  );

  useEffect(() => {
    applySearch(searchTerm);
  }, [searchTerm, applySearch]);

  // TODO: If re-enabling this code, use getShowPeriodBreakdown(data) instead of data.periodReporting
  /*   useEffect(() => {
    const showPeriodBreakdown = getShowPeriodBreakdown(data);
    document.documentElement.style.setProperty(
      '--ks-header-height',
      budgetAvailable && capabilities.budget && filters.budget && filters.actuals
        ? showPeriodBreakdown
          ? '70px'
          : '60px'
        : showPeriodBreakdown
          ? '60px'
          : '50px'
    );
  }, [filters]); */

  useEffect(() => {
    if (columnsData && columnsData.length > 0) {
      const activeColumns = columnsData.filter(
        column => column.compoundColumn === false && column.originType !== 'Total' && column.originType !== 'Difference'
      ).length;
      setColumns(activeColumns);
      const style = createRowStyles(columnsData);
      document.head.appendChild(style);
      return () => {
        document.head.removeChild(style);
      };
    } else {
      setColumns(0);
    }
  }, [columnsData, setColumns]);

  // Layout Draw function, it is memoized to avoid re-renders when scrolling
  const addTotalRow = actions.totalRow;
  const addTotalColumn = capabilities.synthetic && actions.total;
  const addDifference = capabilities.synthetic && actions.difference;

  // Get liveReport value from data (kept for backward compatibility)
  const liveReportValue = (data as any).liveReport ?? false;

  const { renderedHeaders, renderedColumns } = useColumnRenderer(
    data,
    columnsData || [],
    addTotalRow,
    addTotalColumn,
    addDifference,
    capabilities.liveReport && liveReportValue,
    fixFC,
    fixLC,
    reverse === 'true', // Pass reverse state for React keys
    containerHeight // Pass container height for virtualization
  );

  if (!columnsData || columnsData.length === 0) {
    return <div>Incomplete Data or Malformed Data</div>;
  }

  const prefix = prefixClass('nt-layout');
  return (
    <>
      <div className={prefix('header-container')}>{renderedHeaders}</div>
      <div ref={containerRef} id="table-y-scroll" className={prefix('layout-container')}>
        {renderedColumns}
      </div>
    </>
  );
};
export default NTLayout;
